import { useState } from 'react';
import { X, Copy, Check, AlertTriangle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

export default function ErrorModal({ isOpen, onClose, title, message, errorCode }) {
  const { isDark } = useTheme();
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const fullErrorText = `${title}\n${message}${errorCode ? `\nCodice: ${errorCode}` : ''}\nData: ${new Date().toLocaleString('it-IT')}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullErrorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullErrorText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-md rounded-2xl p-6 shadow-2xl ${
        isDark ? 'bg-gray-900 border border-gray-700' : 'bg-white'
      }`}>
        {/* Close button */}
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
            isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>

        {/* Title */}
        <h3 className={`text-xl font-bold text-center mb-2 ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>
          {title || 'Errore'}
        </h3>

        {/* Message */}
        <div className={`rounded-xl p-4 mb-4 ${
          isDark ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-sm ${isDark ? 'text-red-200' : 'text-red-700'}`}>
            {message}
          </p>
          {errorCode && (
            <p className={`text-xs mt-2 font-mono ${isDark ? 'text-red-300/60' : 'text-red-500/60'}`}>
              Codice: {errorCode}
            </p>
          )}
        </div>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl mb-3 transition-all ${
            copied
              ? 'bg-green-500 text-white'
              : isDark 
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              Copiato!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copia errore per assistenza
            </>
          )}
        </button>

        {/* Close button */}
        <button
          onClick={onClose}
          className="w-full py-3 px-4 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
        >
          Chiudi
        </button>
      </div>
    </div>
  );
}
